import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { Section174AdjService } from './section174-adj.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-section174-adj',
    templateUrl: './section174-adj.component.html',
    styleUrls: ['./section174-adj.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class Section174AdjComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private isInitialized = false;

  baseURLs: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};
  gridData: any = {};

  minimise = false;
  crudLoading = false;
  section174List: any[] = [];

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private section174Service: Section174AdjService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.tryInit();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header;
    this.USER_SETTINGS = header.userSettingsData;
    this.SERVER_CONFIG = header.serverConfigData;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    if (Array.isArray(rawRow)) {
      this.gridData = rawRow;
      this.rowData = rawRow[0]?.data || rawRow[0] || {};
    } else {
      this.rowData = rawRow;
      this.gridData = rawRow;
    }
    this.cdr.detectChanges();
  }

  private tryInit() {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) return;
    this.isInitialized = true;
    this.init();
  }

  private init() {
    this.crudLoading = false;
    this.section174List = [];

    let entities: any[] | any;
    if (Array.isArray(this.gridData)) {
      entities = _.clone(this.gridData);
    } else {
      entities = [{ data: this.rowData }];
    }

    if (!Array.isArray(entities)) {
      const obj: any = {};
      obj.row_id = (entities as any)?.object_id;
      obj.data = entities;
      entities = [obj];
    }

    for (const g in entities) {
      const entityData = entities[g].data || entities[g];
      const tempobj: any = {};
      tempobj.Book = entityData.BOOK_AMT;
      tempobj.Section_174_Book = entityData.BOOK_SEC_174;
      tempobj.OLD_Section_174_Book = entityData.BOOK_SEC_174;
      tempobj.Tax_Adj = entityData.TAX_ADJ;
      tempobj.Tax_Adj_174 = entityData.TAX_ADJ_SEC_174;
      tempobj.OLD_Tax_Adj_174 = entityData.TAX_ADJ_SEC_174;
      tempobj.LEID = entityData.LEID;
      tempobj.CDR_NO = entityData.CDR_NO;
      tempobj.CURR = entityData.FC;
      tempobj.ME = entityData.ME;
      tempobj.REPORTING_PERIOD = entityData.REPORTING_PERIOD;
      tempobj.LOCAL_ACCT = entityData.LOCAL_ACCT;
      tempobj.LOCAL_ACCT_DESC = entityData.LOCAL_ACCT_DESC;
      tempobj.SCHDM_DESC = entityData.SCHDM_DESC;
      tempobj.COMBINATION_KEY = entityData.COMBINATION_KEY;
      tempobj.LOCAL_ACCT_KEY = entityData.LOCAL_ACCT_KEY;
      tempobj.LOCAL_CHART_KEY = entityData.LOCAL_CHART_KEY;
      this.section174List.push(tempobj);
    }
    this.cdr.detectChanges();
  }

  applysection174book(index: number) {
    this.section174List[index].Section_174_Book = this.section174List[index].Book;
    this.cdr.detectChanges();
  }

  save() {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }
    this.crudLoading = true;
    this.cdr.detectChanges();

    const sendobj: any[] = [];
    for (let s = 0; s < this.section174List.length; s++) {
      const dummyJson: any = {};
      dummyJson.TAX_YEAR = this.rowData.TAX_YEAR ?? this.globalService?.globalParams?.tax_year;
      dummyJson.SCENARIO = this.rowData.SCENARIO ?? this.globalService?.globalParams?.scenario;
      dummyJson.JCD_KEY = this.rowData.JCD_KEY ?? this.globalService?.globalParams?.jcd_key;
      dummyJson.OLD_SECTION174_BOOK = this.section174List[s].OLD_Section_174_Book;
      dummyJson.NEW_SECTION174_BOOK = this.section174List[s].Section_174_Book;
      dummyJson.COMBINATION_KEY = this.section174List[s].COMBINATION_KEY;
      dummyJson.LOCAL_ACCT_KEY = this.section174List[s].LOCAL_ACCT_KEY;
      dummyJson.LOCAL_CHART_KEY = this.section174List[s].LOCAL_CHART_KEY;
      sendobj.push(dummyJson);
    }

    this.section174Service.saveSection174(this.GENERAL_CONFIG.base_url, sendobj, this.globalService, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.crudLoading = false;
        if (result.callSuccess !== '1') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage || 'Error saving data', 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('Data has been saved successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  reset() {
    this.isInitialized = false;
    this.tryInit();
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
